import { EndpointService } from "@/types/api/api";
import type { LoginResponse } from "@/types/api/contracts/login";
import axios, { type AxiosRequestConfig, type AxiosResponse } from "axios";
import qs from "qs";

export interface OperationParams {
  params?: object;
  bustCache?: boolean;
  cookie?: string;
  baseUrl?: string;
  abortSignal?: AbortSignal;
  idempotencyKey?: string;
  headers?: object;
  venueSpecific?: boolean;
  arrayFormat?: "comma" | "indices";
  endpointService?: EndpointService;
}
export interface IAPI {
  selectedVenue: string | number | null;
  getFile: (url: string, params?: OperationParams) => Promise<Blob>;

  getAPI: (url: string, params?: OperationParams) => Promise<unknown>;
  postAPI: (url: string, params?: OperationParams) => Promise<unknown>;

  get: <T>(url: string, params?: OperationParams) => Promise<T>;
  post: <T>(url: string, params?: OperationParams) => Promise<T>;
  patch: <T>(url: string, params?: OperationParams) => Promise<T>;
  delete: <T>(url: string, params?: OperationParams) => Promise<T>;
  login: (params?: OperationParams) => Promise<unknown>;
  OTPLogin: (params?: OperationParams) => Promise<unknown>;
  retrieveToken: (params?: OperationParams) => Promise<unknown>;
}

type APICall = {
  method: "get" | "put" | "post" | "patch" | "delete";
  url: string;
  params?: object;
  bustCache?: boolean;
  cookie?: string;
  baseUrl?: string;
  headers?: object;
  ignoreEmployee?: boolean;
  abortSignal?: AbortSignal;
  idempotencyKey?: string;
  arrayFormat?: "comma" | "indices";
  endpointService?: EndpointService;
};

export class API implements IAPI {
  selectedVenue: string | number;

  public async get<T>(
    url: string,
    {
      params = {},
      bustCache = false,
      cookie = undefined,
      baseUrl = undefined,
      headers = undefined,
      ignoreEmployee = false,
      abortSignal = undefined,
      idempotencyKey = null,
      arrayFormat = "comma",
      endpointService = EndpointService.Core,
    } = {},
  ): Promise<T> {
    try {
      return await this.execute({
        method: "get",
        url,
        params,
        endpointService,
        bustCache,
        cookie,
        baseUrl,
        headers,
        ignoreEmployee,
        abortSignal,
        idempotencyKey,
        arrayFormat: arrayFormat as "comma" | "indices",
      });
    } catch (err) {
      console.error(err);
      throw err.response.data;
    }
  }

  public async post<T>(
    url: string,
    {
      params = {},
      bustCache = false,
      cookie = undefined,
      baseUrl = undefined,
      headers = undefined,
      ignoreEmployee = false,
      idempotencyKey = null,
      endpointService = EndpointService.Core,
    } = {},
  ): Promise<T> {
    try {
      return await this.execute({
        method: "post",
        url,
        params,
        endpointService,
        bustCache,
        cookie,
        baseUrl,
        headers,
        ignoreEmployee,
        idempotencyKey,
      });
    } catch (err) {
      console.error(err);
      throw err.response.data;
    }
  }

  public async patch<T>(
    url: string,
    {
      params = {},
      bustCache = false,
      cookie = undefined,
      baseUrl = undefined,
      headers = undefined,
      ignoreEmployee = false,
      idempotencyKey = null,
      endpointService = EndpointService.Core,
    } = {},
  ): Promise<T> {
    try {
      return await this.execute({
        method: "patch",
        url,
        params,
        endpointService,
        bustCache,
        cookie,
        baseUrl,
        headers,
        ignoreEmployee,
        idempotencyKey,
      });
    } catch (err) {
      console.error(err);
      throw err.response.data;
    }
  }

  public async delete<T>(
    url: string,
    {
      params = {},
      bustCache = false,
      cookie = undefined,
      baseUrl = undefined,
      headers = undefined,
      ignoreEmployee = false,
      idempotencyKey = null,
      endpointService = EndpointService.Core,
    } = {},
  ): Promise<T> {
    try {
      return await this.execute({
        method: "delete",
        url,
        params,
        bustCache,
        cookie,
        baseUrl,
        headers,
        ignoreEmployee,
        idempotencyKey,
        endpointService,
      });
    } catch (err) {
      console.error(err);
      throw err.response.data;
    }
  }

  public async login({
    params = {},
    endpointService = EndpointService.Core,
    baseUrl = undefined,
  } = {}): Promise<AxiosResponse<LoginResponse>> {
    try {
      return await axios.post(`${baseUrl ?? ""}/api/dines/login`, params, {
        headers: {
          "Content-Type": "application/json",
          "X-USE-ENDPOINT-SERVICE": endpointService,
        },
      });
    } catch (err) {
      throw err.response.data;
    }
  }

  public async OTPLogin({
    params = {},
    endpointService = EndpointService.Core,
    baseUrl = undefined,
  } = {}): Promise<AxiosResponse<LoginResponse>> {
    try {
      return await axios.post(`${baseUrl ?? ""}/api/dines/otp_login`, params, {
        headers: {
          "Content-Type": "application/json",
          "X-USE-ENDPOINT-SERVICE": endpointService,
        },
      });
    } catch (err) {
      throw err.response.data;
    }
  }

  public async retrieveToken({
    params = {},
    endpointService = EndpointService.Core,
    baseUrl = undefined,
  } = {}): Promise<AxiosResponse<LoginResponse>> {
    try {
      return await axios.post(
        `${baseUrl ?? ""}/api/dines/retrieve_token`,
        params,
        {
          headers: {
            "Content-Type": "application/json",
            "X-USE-ENDPOINT-SERVICE": endpointService,
          },
        },
      );
    } catch (err) {
      throw err.response.data;
    }
  }

  public async getFile(
    url: string,
    { endpointService = EndpointService.Core } = {},
  ): Promise<Blob> {
    try {
      return await axios.post(
        "/api/dines/file",
        {
          file: url,
        },
        {
          headers: {
            "X-USE-ENDPOINT-SERVICE": endpointService,
          },
        },
      );
    } catch (err) {
      throw err.response.data;
    }
  }

  public async getAPI(url: string, { params = {} } = {}): Promise<unknown> {
    try {
      const results = await axios.get(url, {
        params,
      });
      return results;
    } catch (err) {
      throw err.response.data;
    }
  }

  public async postAPI(url: string, { params = {} } = {}): Promise<unknown> {
    try {
      const results = await axios.post(url, {
        ...params,
      });
      return results;
    } catch (err) {
      return {
        data: null,
        error: err.response.data,
      };
    }
  }

  public async request(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<unknown>> {
    const api = axios.create({});
    return api.request(config);
  }

  private execute<T>({
    method,
    url,
    params = null,
    endpointService = EndpointService.Core,
    bustCache = false,
    cookie = undefined,
    baseUrl = process.env.BASE_URL,
    headers = undefined,
    ignoreEmployee = false,
    abortSignal = undefined,
    idempotencyKey = null,
    arrayFormat = "comma",
  }: APICall): Promise<T> {
    const api = axios.create({
      method,
      signal: abortSignal,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        ...(cookie !== undefined && { Cookie: cookie }),
        "X-USE-ENDPOINT-SERVICE": endpointService,
        "X-BUST-CACHE": bustCache,
        "X-IGNORE-EMPLOYEE": ignoreEmployee,
        "X-IDEMPOTENCY-KEY": idempotencyKey,
        ...(headers !== undefined && headers),
      },
      baseURL: `${baseUrl}/api/dines/api${
        url.endsWith("/") ? url.slice(0, -1) : url
      }`,
      paramsSerializer: (qsParams) => {
        return qs.stringify(qsParams, { arrayFormat });
      },
    });

    return api.request({
      ...(method === "get" &&
        params && {
          params,
        }),
      ...((method === "post" || method === "patch" || method === "put") &&
        params && {
          data: params,
        }),
    });
  }
}

export const apiService = new API();
